import { DirectFeatureAccessUserGroupId, SpecialUserGroupId } from '../../user-groups/types';
import { AccessSubjectType } from '../types';
import { FeatureAccessMessage, FeatureAccessMessageType, FeatureId } from './types';

export const userGroupsConsideredMicroWorkers = [
  SpecialUserGroupId.MICROWORK,
  DirectFeatureAccessUserGroupId.AALTO,
] as const;

/**
 * OA: A temporary, hard coded data source for feature accesses
 * Currently this combines two things:
 * 1) The access to the feature.
 * 2) The status of the feature development ("Coming soon").
 * These probably should be separated to prevent things getting super messy
 */

// A TEAM row has a STAFF row beside it only where the feature's routes answer by the caller's own
// access, as for any user, so a member of STAFF outside TEAM sees there only what their own rows give
// (the screener filters companies and scenarios this way). Features that reach customers' data through
// TEAM (portfolio search, the company platform, all weight sets, the API) and internal tools not yet
// reviewed stay TEAM's.

type FeatureAccessRow<T extends FeatureId> = [
  SpecialUserGroupId | DirectFeatureAccessUserGroupId,
  FeatureAccessMessageType,
  T,
];

// Refers to searching for groups and portfolios in the main search
const SEARCHBAR: Array<FeatureAccessRow<FeatureId.SEARCHBAR>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.SEARCHBAR],
  [SpecialUserGroupId.EVERYONE, FeatureAccessMessageType.SUBSCRIPTION_NEEDED, FeatureId.SEARCHBAR],
  [SpecialUserGroupId.CLIENTS, FeatureAccessMessageType.AVAILABLE, FeatureId.SEARCHBAR],
  [DirectFeatureAccessUserGroupId.TAALERI, FeatureAccessMessageType.AVAILABLE, FeatureId.SEARCHBAR],
  [SpecialUserGroupId.ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS, FeatureAccessMessageType.AVAILABLE, FeatureId.SEARCHBAR],
];

const SCREENER: Array<FeatureAccessRow<FeatureId.SCREENER>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.SCREENER],
  [SpecialUserGroupId.STAFF, FeatureAccessMessageType.AVAILABLE, FeatureId.SCREENER],
  [SpecialUserGroupId.CLIENTS, FeatureAccessMessageType.COMING_SOON, FeatureId.SCREENER],
];

const COMPANY_PLATFORM: Array<
  FeatureAccessRow<
    | FeatureId.COMPANY_PLATFORM_DASHBOARD
    | FeatureId.COMPANY_PLATFORM_INPUT_PRODUCTS
    | FeatureId.COMPANY_PLATFORM_INPUT_TARGETS_INSIGHTS
  >
> = [
  [
    SpecialUserGroupId.COMPANY_PLATFORM_STANDARD_CLIENTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.COMPANY_PLATFORM_DASHBOARD,
  ],
  [
    SpecialUserGroupId.COMPANY_PLATFORM_FREE_CLIENTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.COMPANY_PLATFORM_DASHBOARD,
  ],
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.COMPANY_PLATFORM_DASHBOARD],
  [
    SpecialUserGroupId.COMPANY_PLATFORM_STANDARD_CLIENTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.COMPANY_PLATFORM_INPUT_PRODUCTS,
  ],
  [
    SpecialUserGroupId.COMPANY_PLATFORM_FREE_CLIENTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.COMPANY_PLATFORM_INPUT_PRODUCTS,
  ],
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.COMPANY_PLATFORM_INPUT_PRODUCTS],
  [
    SpecialUserGroupId.COMPANY_PLATFORM_STANDARD_CLIENTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.COMPANY_PLATFORM_INPUT_TARGETS_INSIGHTS,
  ],
  [
    SpecialUserGroupId.COMPANY_PLATFORM_FREE_CLIENTS,
    FeatureAccessMessageType.SUBSCRIPTION_NEEDED,
    FeatureId.COMPANY_PLATFORM_INPUT_TARGETS_INSIGHTS,
  ],
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.COMPANY_PLATFORM_INPUT_TARGETS_INSIGHTS],
];

// Should be combined with the "export data" feature accesses, once that feature is finished
const DOWNLOAD_AS_EXCEL = copyRows(SEARCHBAR, FeatureId.DOWNLOAD_AS_EXCEL);
const EDIT_CURRENT_WEIGHT_SET = copyRows(SEARCHBAR, FeatureId.EDIT_CURRENT_WEIGHT_SET);
const TIME_TRACKING = copyRows(SEARCHBAR, FeatureId.TIME_TRACKING);
const DOWNLOAD_IMAGE = copyRows(SEARCHBAR, FeatureId.DOWNLOAD_IMAGE);

// Granting to ANONYMOUS (not EVERYONE) keeps these lenses visible to logged-out visitors of the open
// platform while allowing them to be hidden per signed-in customer group: EVERYONE is an implicit
// ancestor of every user, so a grant there could never be removed via the group admin toggles, whereas
// ANONYMOUS is added only for unauthenticated requests. Signed-in customers get these lenses via an
// explicit group link (EU_ELIGIBILITIES / SDG_ALIGNMENT / NET_IMPACT_USERS) instead.
const EU_TAXONOMY: Array<FeatureAccessRow<FeatureId.EU_TAXONOMY>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.EU_TAXONOMY],
  [SpecialUserGroupId.EU_ELIGIBILITIES, FeatureAccessMessageType.AVAILABLE, FeatureId.EU_TAXONOMY],
  [SpecialUserGroupId.ANONYMOUS, FeatureAccessMessageType.AVAILABLE, FeatureId.EU_TAXONOMY],
];

const SDG_ALIGNMENT: Array<FeatureAccessRow<FeatureId.SDG_ALIGNMENT>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.SDG_ALIGNMENT],
  [SpecialUserGroupId.SDG_ALIGNMENT, FeatureAccessMessageType.AVAILABLE, FeatureId.SDG_ALIGNMENT],
  [SpecialUserGroupId.ANONYMOUS, FeatureAccessMessageType.AVAILABLE, FeatureId.SDG_ALIGNMENT],
];

const NET_IMPACT: Array<FeatureAccessRow<FeatureId.NET_IMPACT>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.NET_IMPACT],
  [SpecialUserGroupId.NET_IMPACT_USERS, FeatureAccessMessageType.AVAILABLE, FeatureId.NET_IMPACT],
  [SpecialUserGroupId.ANONYMOUS, FeatureAccessMessageType.AVAILABLE, FeatureId.NET_IMPACT],
];

const IMPACT_EXPORT: Array<FeatureAccessRow<FeatureId.IMPACT_EXPORT>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.IMPACT_EXPORT],
  [SpecialUserGroupId.IMPACT_EXPORT, FeatureAccessMessageType.AVAILABLE, FeatureId.IMPACT_EXPORT],
];

const EU_PAI: Array<FeatureAccessRow<FeatureId.EU_PAI>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.EU_PAI],
  [SpecialUserGroupId.EU_PAI, FeatureAccessMessageType.AVAILABLE, FeatureId.EU_PAI],
];

const DOWNLOAD_REPORT: Array<FeatureAccessRow<FeatureId.DOWNLOAD_REPORT>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.COMING_SOON, FeatureId.DOWNLOAD_REPORT],
  [SpecialUserGroupId.EVERYONE, FeatureAccessMessageType.SUBSCRIPTION_NEEDED, FeatureId.DOWNLOAD_REPORT],
  [SpecialUserGroupId.CLIENTS, FeatureAccessMessageType.COMING_SOON, FeatureId.DOWNLOAD_REPORT],
  [DirectFeatureAccessUserGroupId.TAALERI, FeatureAccessMessageType.COMING_SOON, FeatureId.DOWNLOAD_REPORT],
  [
    SpecialUserGroupId.ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS,
    FeatureAccessMessageType.COMING_SOON,
    FeatureId.DOWNLOAD_REPORT,
  ],
];

const EXPORT_DATA: Array<FeatureAccessRow<FeatureId.EXPORT_DATA>> = copyRows(DOWNLOAD_REPORT, FeatureId.EXPORT_DATA);

const PRODUCT_VIEW: Array<FeatureAccessRow<FeatureId.PRODUCT_VIEW>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_VIEW],
  [SpecialUserGroupId.STAFF, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_VIEW],
  [SpecialUserGroupId.EVERYONE, FeatureAccessMessageType.SUBSCRIPTION_NEEDED, FeatureId.PRODUCT_VIEW],
  [SpecialUserGroupId.CLIENTS, FeatureAccessMessageType.CURRENT_SUBCRIPTION_NOT_ENOUGH, FeatureId.PRODUCT_VIEW],
  [DirectFeatureAccessUserGroupId.TAALERI, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_VIEW],
  [DirectFeatureAccessUserGroupId.AKTIA, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_VIEW],
  [DirectFeatureAccessUserGroupId.ARISAIG, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_VIEW],
  [DirectFeatureAccessUserGroupId.RLAM, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_VIEW],
  [
    SpecialUserGroupId.ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.PRODUCT_VIEW,
  ],
];
const PRODUCT_SEARCH: Array<FeatureAccessRow<FeatureId.PRODUCT_SEARCH>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [SpecialUserGroupId.STAFF, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [SpecialUserGroupId.MICROWORK, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [DirectFeatureAccessUserGroupId.STATISTA, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [SpecialUserGroupId.EVERYONE, FeatureAccessMessageType.SUBSCRIPTION_NEEDED, FeatureId.PRODUCT_SEARCH],
  [SpecialUserGroupId.CLIENTS, FeatureAccessMessageType.CURRENT_SUBCRIPTION_NOT_ENOUGH, FeatureId.PRODUCT_SEARCH],
  [DirectFeatureAccessUserGroupId.TAALERI, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [DirectFeatureAccessUserGroupId.AKTIA, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [DirectFeatureAccessUserGroupId.ARISAIG, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [DirectFeatureAccessUserGroupId.AALTO, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [DirectFeatureAccessUserGroupId.RLAM, FeatureAccessMessageType.AVAILABLE, FeatureId.PRODUCT_SEARCH],
  [
    SpecialUserGroupId.ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS,
    FeatureAccessMessageType.AVAILABLE,
    FeatureId.PRODUCT_SEARCH,
  ],
];

const EDIT_ALL_WEIGHT_SETS: Array<FeatureAccessRow<FeatureId.EDIT_ALL_WEIGHT_SETS>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.EDIT_ALL_WEIGHT_SETS],
  [SpecialUserGroupId.EDIT_ALL_WEIGHTSETS, FeatureAccessMessageType.AVAILABLE, FeatureId.EDIT_ALL_WEIGHT_SETS],
];

// NOTE: Write endpoints required EDIT_ALL_WEIGHT_SETS feature as well
const API: Array<FeatureAccessRow<FeatureId.API>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.API],
  [SpecialUserGroupId.API, FeatureAccessMessageType.AVAILABLE, FeatureId.API],
];

const BLANKET_LIMITED: Array<FeatureAccessRow<FeatureId.BLANKET_LIMITED>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.BLANKET_LIMITED],
  [SpecialUserGroupId.MICROWORK, FeatureAccessMessageType.AVAILABLE, FeatureId.BLANKET_LIMITED],
  [DirectFeatureAccessUserGroupId.AALTO, FeatureAccessMessageType.AVAILABLE, FeatureId.BLANKET_LIMITED],
  [DirectFeatureAccessUserGroupId.STATISTA, FeatureAccessMessageType.AVAILABLE, FeatureId.BLANKET_LIMITED],
];

const ADVANCED_MIXIN_TEMPLATES: Array<FeatureAccessRow<FeatureId.ADVANCED_MIXIN_TEMPLATES>> = [
  [SpecialUserGroupId.MIXIN_ADVANCED_USERS, FeatureAccessMessageType.AVAILABLE, FeatureId.ADVANCED_MIXIN_TEMPLATES],
];

// OH 22.10.24: Temporary feature access groups before publishing reasonings features to all customers.
const CSRD_REASONINGS: Array<FeatureAccessRow<FeatureId.CSRD_REASONINGS | FeatureId.GISE_STATEMENTS>> = [
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.CSRD_REASONINGS],
  [SpecialUserGroupId.TEAM, FeatureAccessMessageType.AVAILABLE, FeatureId.GISE_STATEMENTS],
  [SpecialUserGroupId.EVERYONE, FeatureAccessMessageType.AVAILABLE, FeatureId.CSRD_REASONINGS],

  [SpecialUserGroupId.CSRD_REASONINGS, FeatureAccessMessageType.AVAILABLE, FeatureId.CSRD_REASONINGS],
  [SpecialUserGroupId.GISE_STATEMENTS, FeatureAccessMessageType.AVAILABLE, FeatureId.GISE_STATEMENTS],
];

const featureAccessRows: Array<FeatureAccessRow<FeatureId>> = [
  ...TIME_TRACKING,
  ...DOWNLOAD_REPORT,
  ...EXPORT_DATA,
  ...EU_TAXONOMY,
  ...SDG_ALIGNMENT,
  ...NET_IMPACT,
  ...IMPACT_EXPORT,
  ...EU_PAI,
  ...DOWNLOAD_AS_EXCEL,
  ...DOWNLOAD_IMAGE,
  ...SEARCHBAR,
  ...PRODUCT_VIEW,
  ...PRODUCT_SEARCH,
  ...EDIT_CURRENT_WEIGHT_SET,
  ...EDIT_ALL_WEIGHT_SETS,
  ...API,
  ...SCREENER,
  ...BLANKET_LIMITED,
  ...ADVANCED_MIXIN_TEMPLATES,
  ...COMPANY_PLATFORM,
  ...CSRD_REASONINGS,
];

export const allFeatureAccesses: FeatureAccessMessage[] = featureAccessRows.map(row => ({
  subjectId: row[0],
  subjectType: AccessSubjectType.USER_GROUP,
  type: row[1],
  featureId: row[2],
}));

function copyRows<T extends FeatureId, K extends FeatureId>(
  accessesToCopy: Array<FeatureAccessRow<T>>,
  featureId: K,
): Array<FeatureAccessRow<K>> {
  return accessesToCopy.map(accessRow => [accessRow[0], accessRow[1], featureId]);
}
